import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getSpecialistAllSlots } from '../api/client';
import Footer from '../components/Footer';

export default function BookPage() {
  const { clinicId, serviceId, specId } = useParams();
  const navigate = useNavigate();

  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [slotsByDate, setSlotsByDate] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchSlots() {
      try {
        setLoading(true);
        const response = await getSpecialistAllSlots(specId);
        
        if (!response.availableSlots) {
          console.error('Неожиданный формат ответа:', response);
          throw new Error('Неверный формат данных от сервера');
        }

        setSlotsByDate(response.availableSlots);
        setError(null);
      } catch (err) {
        console.error('Ошибка загрузки слотов:', err);
        console.error('Детали ошибки:', {
          message: err.message,
          response: err.response?.data,
          status: err.response?.status
        });
        setError('Не удалось загрузить доступное время. Пожалуйста, попробуйте позже.');
      } finally {
        setLoading(false);
      }
    }
    fetchSlots();
  }, [specId]);

  const handleSelectSlot = (slot) => setSelectedSlot(slot);
  const handleSubmit = () => {
    if (selectedSlot) {
      navigate(`/booking/${clinicId}/${serviceId}/${specId}/confirm?slot=${encodeURIComponent(JSON.stringify(selectedSlot))}`);
    }
  };

  const dates = Object.keys(slotsByDate).sort();
  const today = new Date().toISOString().split('T')[0];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-red-500 text-center">
          <p className="text-xl font-semibold mb-2">Ошибка</p>
          <p>{error}</p>
          <button 
            onClick={() => navigate(`/${clinicId}/${serviceId}/${specId}`)}
            className="mt-4 px-4 py-2 bg-secondary text-white rounded-md hover:bg-secondary/90 transition"
          >
            Назад
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 text-gray-800 relative pb-16">
      <header className="bg-white shadow p-4 sticky top-0 z-10">
        <div className="flex items-center gap-4 max-w-xl mx-auto relative">
          <button
            onClick={() => navigate(`/${clinicId}/${serviceId}/${specId}`)}
            className="absolute left-0 text-gray-600 hover:bg-gray-100 rounded-full w-10 h-10 flex items-center justify-center transition"
          >
            ←
          </button>
          <h1 className="text-center flex-grow text-lg font-semibold">Выбрать дату и время</h1>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-4 py-6">
        <h2 className="text-base font-semibold mb-2">Доступные даты</h2>
        <div className="flex gap-2 overflow-x-auto pb-2">
          {dates.length > 0 ? (
            dates.map((dateStr) => (
              <button
                key={dateStr}
                onClick={() => {
                  setSelectedDate(dateStr);
                  setSelectedSlot(null);
                }}
                className={`px-4 py-2 rounded-full border text-sm whitespace-nowrap transition ${
                  selectedDate === dateStr
                    ? 'bg-primary text-white border-primary'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-primary'
                }`}
              >
                {new Date(dateStr).toLocaleDateString('ru-RU', {
                  weekday: 'short',
                  day: 'numeric',
                  month: 'short',
                })}
              </button>
            ))
          ) : (
            <div className="text-sm text-gray-500">Нет доступных дат</div>
          )}
        </div>

        <h2 className="text-base font-semibold mt-6 mb-2">Время</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {selectedDate && slotsByDate[selectedDate]?.length > 0 ? (
            slotsByDate[selectedDate].map((slot, i) => (
              <button
                key={i}
                className={`text-sm py-2 px-4 rounded-lg border transition ${
                  selectedSlot?.start_time === slot.start_time
                    ? 'bg-primary text-white border-primary'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-primary'
                }`}
                onClick={() => handleSelectSlot(slot)}
              >
                {new Date(slot.start_time).toLocaleTimeString([], { 
                  hour: '2-digit', 
                  minute: '2-digit',
                  hour12: false 
                })}
              </button>
            ))
          ) : (
            <div className="col-span-full text-sm text-gray-500">
              {selectedDate ? 'Нет доступных слотов' : 'Выберите дату'}
            </div>
          )}
        </div>

        <div className="mt-12">
          <button
            disabled={!selectedSlot}
            onClick={handleSubmit}
            className="w-full px-4 py-2 bg-secondary text-white rounded-md hover:bg-secondary/90 transition"
          >
            Записаться
          </button>
        </div>
      </main>
      <Footer />
    </div>
  );
}
